
import React from "react";
import { Button } from "@/components/ui/button";
import { Volume2, Pause, Loader2, AlertCircle } from "lucide-react";
import { PrayerVoice } from "@/hooks/prayer/usePrayerVoices";
import { Progress } from "@/components/ui/progress";

interface AudioControlsProps {
  isGeneratingAudio: boolean;
  isPlaying: boolean;
  isAudioReady: boolean;
  error: string | null;
  isPrayerTooLong: boolean;
  selectedVoice: PrayerVoice;
  loadingProgress?: number; // New prop for loading progress
  isIOS?: boolean; // New prop for iOS detection
  onPlayPause: () => void;
}

const AudioControls: React.FC<AudioControlsProps> = ({
  isGeneratingAudio,
  isPlaying,
  isAudioReady,
  error,
  isPrayerTooLong,
  selectedVoice,
  loadingProgress = 0,
  isIOS = false,
  onPlayPause
}) => {
  // Determine button state and content
  const isButtonDisabled = isGeneratingAudio || !!error;
  const showProgress = isGeneratingAudio || (loadingProgress > 0 && loadingProgress < 100);
  
  // Button content based on state
  const getButtonContent = () => {
    if (isGeneratingAudio) {
      return (
        <>
          <Loader2 className="h-5 w-5 text-heaven-gold animate-spin" />
          <span className="hidden sm:inline">Generating...</span>
        </>
      );
    }
    
    if (isPlaying) {
      return (
        <>
          <Pause className="h-5 w-5 text-heaven-gold" />
          <span className="hidden sm:inline">Pause</span>
        </>
      );
    }
    
    return (
      <>
        <Volume2 className="h-5 w-5 text-heaven-gold" />
        <span className="hidden sm:inline">
          {isAudioReady ? "Play" : `Listen with ${selectedVoice.name}`}
        </span>
      </>
    );
  };

  return (
    <div className="flex flex-col items-start gap-2 w-full sm:w-auto">
      <div className="flex items-center gap-2 w-full sm:w-auto">
        {isPrayerTooLong && (
          <div className="text-xs text-amber-600 flex items-center mr-2">
            <AlertCircle className="h-3 w-3 mr-1" />
            Audio will be shortened
          </div>
        )}
        
        {error && (
          <div className="text-xs text-red-600 flex items-center mr-2">
            <AlertCircle className="h-3 w-3 mr-1" />
            Audio error
          </div>
        )}
        
        <Button
          onClick={onPlayPause}
          variant="outline"
          className={`flex items-center gap-2 ${isPlaying ? 'bg-heaven-gold/10' : ''} ${isIOS && isAudioReady ? 'animate-pulse' : ''}`}
          disabled={isButtonDisabled}
          aria-label={isPlaying ? "Pause audio" : "Play audio"}
        >
          {getButtonContent()}
        </Button>
        
        {/* iOS-specific message */}
        {isIOS && isAudioReady && !isPlaying && (
          <span className="text-xs text-gray-500 ml-2">Tap to play</span>
        )}
      </div>
      
      {/* Progress bar for audio generation and loading */}
      {showProgress && (
        <div className="w-full sm:w-48 flex items-center gap-2">
          <Progress value={loadingProgress} className="h-2" />
          <span className="text-xs text-gray-500">{loadingProgress}%</span>
        </div>
      )}
    </div>
  );
};

export default AudioControls;
